/* eslint-disable @typescript-eslint/no-explicit-any */
import { createApp } from 'vue';
import * as VueRouter from 'vue-router';
import 'vuetify/styles';
import '@fontsource-variable/inter/wght.css';
import { createVuetify } from 'vuetify';
import * as hrRH from 'date-fns/locale/hr';
import { hr } from 'vuetify/locale';
import DateFnsAdapter from '@date-io/date-fns'

import './assets/global.css';

import App from './App.vue';
import { routes } from './routes/routes';
import { isAdminRole, Role, User } from './models/User';
import { usePushNotifications } from './utils/pushNotifications';
import { clearMismatchedSubscription } from './utils/webPush';
import { setDescription, setMetaTitle, setMetaUrl } from './utils/seo';
import { pinia } from './stores/pinia';
import { getMockRole } from './stores/userStore';
import { getLastVersion, isVersionStale } from './utils/checkVersion';
import { isChunkLoadError, tryReloadOnChunkError } from './utils/handleChunkLoadError';
import { installPrefetchDirective } from './utils/prefetchRoute';

const vuetify = createVuetify({
    date: {
        adapter: DateFnsAdapter as any,
        locale: hrRH,
    },
    locale: {
        locale: 'hr',
        messages: {
            hr,
        },
    },
    theme: {
        defaultTheme: 'dark',
        themes: {
            dark: {
                dark: true,
                colors: {
                    background: '#121212',
                    surface: '#202020',
                    primary: '#DF73FF',
                    accent: '#7289DA',
                    secondary: '#7BAFD4',
                    info: '#7BAFD4',
                    danger: '#A20925',
                    'on-background': '#FEFEFA',
                },
            }
        }
    },
});

const router = VueRouter.createRouter({
    history: VueRouter.createWebHistory(),
    routes,
    scrollBehavior(_to, _from, savedPosition) {
        return savedPosition ?? { top: 0 };
    },
});

router.onError((error, to) => {
    if (isChunkLoadError(error)) {
        tryReloadOnChunkError(to.fullPath);
    }
});

// If the background poller has already observed a newer deploy than what this
// tab booted on, bounce to a fresh document for the destination URL instead of
// letting the SPA try (and fail) to lazy-load a removed chunk. Registered
// first so it short-circuits role checks and meta updates.
router.beforeEach((to) => {
    if (isVersionStale()) {
        const url = router.resolve(to).href;
        window.location.assign(url);
        return false;
    }
});

router.beforeEach(async (to) => {
    if (!to.meta?.role) {
        return;
    }

    await pinia.state.value.user?.isUserResolvedPromise;

    if (to.meta.role) {
        const user = pinia.state.value.user?.user as User | undefined;

        if (user == null) {
            return '/not-found';
        }

        const role = getMockRole() ?? user.role;

        if (isAdminRole(role)) {
            return;
        } else if (role === Role.GM && to.meta.role === Role.Admin) {
            return '/not-found';
        } else if (role === Role.Player && to.meta.role !== Role.Player) {
            return '/not-found';
        }
    }
});

router.beforeEach((to) => {
    const resolvedUrl = `https://odg.hr${router.resolve(to).href}`;
    setMetaTitle(to.meta?.title as string | undefined);
    setDescription(to.meta?.description as string | undefined);
    setMetaUrl(
        to.meta?.url as string | undefined ?? resolvedUrl
    );
});

const app = createApp(App);

app.use(vuetify).use(router).use(pinia);

installPrefetchDirective(app, router);

if (usePushNotifications() && 'serviceWorker' in navigator) {
    // Register the notifications SW eagerly so it's ready to receive pushes
    // for users who already opted in on a prior session. The actual
    // permission prompt is gated behind the Settings toggle — we never want
    // to ask on boot.
    //
    // `clearMismatchedSubscription` first wipes any legacy OneSignal-keyed
    // subscription left attached to scope `/` — without that, ex-OneSignal
    // users see Settings as already-enabled but receive no pushes.
    void (async () => {
        try {
            await clearMismatchedSubscription();
            await navigator.serviceWorker.register('/sw.js');
        } catch (error) {
            // eslint-disable-next-line no-restricted-syntax
            console.error('Failed to register push service worker', error);
        }
    })();
}

app.mount('#app');

// Defer Sentry off the critical path. We accept that errors raised in the
// first ~50ms of session aren't reported — `onErrorCaptured` in App.vue still
// renders the error UI, and chunk-load errors are recovered by `router.onError`.
const initSentry = () => {
    void import('@sentry/vue').then((Sentry) => {
        Sentry.init({
            app,
            dsn: "https://4c818638a71e4f67c03f8bd6b5ed2229@o4510130613911552.ingest.de.sentry.io/4510130615287888",
            // Setting this option to true will send default PII data to Sentry.
            // For example, automatic IP address collection on events
            sendDefaultPii: true,
            sampleRate: 1,
            enabled: !window.location.hostname.includes('localhost'),
            ignoreErrors: [
                /Failed to send a request to the Edge Function/, // supabase-js FunctionsFetchError — client-side network failure, backend logs cover real outages
            ],

            tracesSampleRate: 0.1,
            integrations: [Sentry.browserTracingIntegration({
                router,
            })],
            beforeSendSpan(span) {
                span.data = {
                    ...span.data,
                    'app.version': getLastVersion(),
                };

                return span;
            },

            beforeSend: (event, hint) => {
                const headers = event?.request?.headers;

                if (headers?.['User-Agent']?.includes('HeadlessChrome')) {
                    return null;
                }

                // Chunk-load errors fire on stale tabs after a deploy. `router.onError`
                // and `onErrorCaptured` reload the user onto fresh HTML — the recovery
                // works, so the Sentry report is just noise.
                if (isChunkLoadError(hint?.originalException)) {
                    return null;
                }

                // Supabase auth-js raises AuthRetryableFetchError with status 0 when the
                // browser fetch itself rejects (offline, dropped connection). Matching by
                // `name` avoids importing the supabase type guard from the entry chunk.
                const originalException = hint?.originalException as { name?: string; status?: number } | undefined;
                if (originalException?.name === 'AuthRetryableFetchError' && originalException.status === 0) {
                    return null;
                }

                return event;
            },
        });
    });
};

if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(initSentry, { timeout: 2000 });
} else {
    setTimeout(initSentry, 0);
}

// Icon font is dynamic-imported so it doesn't sit inside the render-blocking
// entry CSS bundle. Vite emits a separate stylesheet that loads in parallel
// with mount; icons FOUC briefly before the woff2 lands.
void import('./assets/mdi.css');
